// =============================================================================
// Module Teams - Repository
// Accès DB pour les équipes et leurs membres
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Team, TeamMember, TeamMemberWithProfile } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL TEAMS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTeams(orgId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('core_teams')
    .select('*')
    .eq('org_id', orgId)
    .order('name');

  if (error) {
    console.error('Error fetching teams:', error);
    return [];
  }

  return data as Team[];
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH TEAM MEMBERS WITH PROFILES
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTeamMembers(teamId: string): Promise<TeamMemberWithProfile[]> {
  const { data, error } = await supabase
    .from('core_team_members')
    .select(`
      id,
      team_id,
      org_member_id,
      role,
      created_at,
      core_organization_members (
        user_id,
        core_profiles (
          id,
          full_name,
          email,
          avatar_url
        )
      )
    `)
    .eq('team_id', teamId);

  if (error) {
    console.error('Error fetching team members:', error);
    return [];
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    team_id: row.team_id,
    org_member_id: row.org_member_id,
    role: row.role,
    created_at: row.created_at,
    user_id: row.core_organization_members?.user_id || '',
    profile: {
      id: row.core_organization_members?.core_profiles?.id || '',
      full_name: row.core_organization_members?.core_profiles?.full_name || null,
      email: row.core_organization_members?.core_profiles?.email || '',
      avatar_url: row.core_organization_members?.core_profiles?.avatar_url || null,
    },
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL TEAM MEMBERS FOR AN ORG (bulk)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchAllTeamMembers(orgId: string): Promise<TeamMemberWithProfile[]> {
  const { data, error } = await supabase
    .from('core_team_members')
    .select(`
      id,
      team_id,
      org_member_id,
      role,
      created_at,
      core_organization_members!inner (
        user_id,
        org_id,
        core_profiles (
          id,
          full_name,
          email,
          avatar_url
        )
      )
    `)
    .eq('core_organization_members.org_id', orgId);

  if (error) {
    console.error('Error fetching all team members:', error);
    return [];
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    team_id: row.team_id,
    org_member_id: row.org_member_id,
    role: row.role,
    created_at: row.created_at,
    user_id: row.core_organization_members?.user_id || '',
    profile: {
      id: row.core_organization_members?.core_profiles?.id || '',
      full_name: row.core_organization_members?.core_profiles?.full_name || null,
      email: row.core_organization_members?.core_profiles?.email || '',
      avatar_url: row.core_organization_members?.core_profiles?.avatar_url || null,
    },
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE TEAM
// ─────────────────────────────────────────────────────────────────────────────
export async function createTeam(
  orgId: string,
  name: string,
  description?: string,
  color?: string
): Promise<Team | null> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('core_teams')
    .insert({
      org_id: orgId,
      name,
      description: description || null,
      color: color || '#6366F1',
      created_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating team:', error);
    return null;
  }

  return data as Team;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE TEAM
// ─────────────────────────────────────────────────────────────────────────────
export async function updateTeam(
  teamId: string,
  updates: Partial<Pick<Team, 'name' | 'description' | 'color' | 'avatar_url'>>
): Promise<boolean> {
  const { error } = await supabase
    .from('core_teams')
    .update(updates)
    .eq('id', teamId);

  if (error) {
    console.error('Error updating team:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE TEAM
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteTeam(teamId: string): Promise<boolean> {
  const { error } = await supabase
    .from('core_teams')
    .delete()
    .eq('id', teamId);

  if (error) {
    console.error('Error deleting team:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD MEMBER TO TEAM
// ─────────────────────────────────────────────────────────────────────────────
export async function addTeamMember(
  teamId: string,
  orgMemberId: string,
  role: 'lead' | 'member' = 'member'
): Promise<boolean> {
  const { error } = await supabase
    .from('core_team_members')
    .insert({
      team_id: teamId,
      org_member_id: orgMemberId,
      role,
    });

  if (error) {
    console.error('Error adding team member:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE MEMBER FROM TEAM
// ─────────────────────────────────────────────────────────────────────────────
export async function removeTeamMember(
  teamId: string,
  orgMemberId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('core_team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('org_member_id', orgMemberId);

  if (error) {
    console.error('Error removing team member:', error);
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE TEAM MEMBER ROLE
// ─────────────────────────────────────────────────────────────────────────────
export async function updateTeamMemberRole(
  teamId: string,
  orgMemberId: string,
  role: 'lead' | 'member'
): Promise<boolean> {
  const { error } = await supabase
    .from('core_team_members')
    .update({ role })
    .eq('team_id', teamId)
    .eq('org_member_id', orgMemberId);

  if (error) {
    console.error('Error updating team member role:', error);
    return false;
  }
  return true;
}
